import { FC, useState } from "react";
import { Controller } from "react-hook-form";
import Select from "react-select";
import { useI18n } from "../../hooks/useTranslations";
import { Car } from "../../http/cars/types";
import { getLanguages, Language } from "../../http/subsidiaries/types";
import CarSubsidiariesDialog from "../CarSubsidiaries/CarSubsidiariesDialog";
import SetIsoCodeDialog from "../subsidiaries/SetIsoCodeDialog";
import { ErrorBox } from "../ui/Boxes";
import Button from "../ui/Button";
import Form from "../ui/Form";
import FormField from "../ui/FormField";
import FormFieldsContainer from "../ui/FormFieldsContainer";
import reactSelectStyles from "../ui/ReactSelectUtils";
import SubmitBar from "../ui/SubmitBar";

export interface AgreementFormBody {
  language: Language;
}

type AgreementFormProps = {
  onSubmit: (form: AgreementFormBody) => Promise<void>;
  isDisabled?: boolean;
  isLoading?: boolean;
  car?: Car;
  onUpdate: () => void;
};

const AgreementForm: FC<AgreementFormProps> = ({ onSubmit, isDisabled, car, onUpdate }: AgreementFormProps) => {
  const i18n = useI18n();

  const [showCarSubsidiariesDialog, setShowCarSubsidiariesDialog] = useState(false);
  const [isoCodeFormOpen, setIsoCodeFormOpen] = useState<boolean>(false);

  const languages = getLanguages(i18n.t);

  const defaultValues = {
    language: languages.find((x) => x.value == car?.carSubsidiary?.subsidiary.defaultLanguage),
  };

  return (
    <>
      <Form<AgreementFormBody>
        submitText={i18n.t.common.downloadFile}
        disabled={isDisabled || !car?.carSubsidiary || !car?.carSubsidiary?.subsidiary.countryIsoCodeAlpha2}
        onSubmit={() => onSubmit}
        defaultValues={defaultValues}
      >
        {({ control }) => {
          return (
            <FormFieldsContainer>
              {!car?.carSubsidiary && (
                <SubmitBar>
                  <ErrorBox text={i18n.t.common.subsidiaryRequiredForDelegation} />
                  <Button buttonProps={{ onClick: () => setShowCarSubsidiariesDialog(true) }}>
                    {i18n.t.common.assignSubsidiary}
                  </Button>
                </SubmitBar>
              )}

              {!car?.carSubsidiary?.subsidiary.countryIsoCodeAlpha2 && (
                <SubmitBar>
                  <ErrorBox text={i18n.t.common.countryIsoCodeRequiredForAgreement} />
                  <Button buttonProps={{ onClick: () => setIsoCodeFormOpen(true) }}>{i18n.t.common.setIsoCode}</Button>
                </SubmitBar>
              )}

              <Controller
                name="language"
                control={control}
                rules={{ required: true }}
                render={({ field, fieldState }) => (
                  <FormField label={i18n.t.common.language} error={fieldState.error}>
                    {({ labelId, isInvalid }) => (
                      <Select
                        {...field}
                        inputId={labelId}
                        options={languages}
                        placeholder={`${i18n.t.common.select}...`}
                        getOptionLabel={(x) => x.label}
                        getOptionValue={(x) => x.value}
                        isDisabled={!car?.carSubsidiary || !car?.carSubsidiary?.subsidiary.countryIsoCodeAlpha2}
                        isClearable
                        styles={reactSelectStyles<Language>(isInvalid)}
                        menuPosition="fixed"
                      />
                    )}
                  </FormField>
                )}
              />

              {!!showCarSubsidiariesDialog && car && (
                <CarSubsidiariesDialog
                  carId={car.id}
                  onClose={() => setShowCarSubsidiariesDialog(false)}
                  onUpdate={onUpdate}
                />
              )}
            </FormFieldsContainer>
          );
        }}
      </Form>

      {isoCodeFormOpen && !!car?.carSubsidiary?.subsidiary && (
        <SetIsoCodeDialog
          subsidiary={car?.carSubsidiary?.subsidiary}
          onClose={() => setIsoCodeFormOpen(false)}
          onUpdate={onUpdate}
        />
      )}
    </>
  );
};

export default AgreementForm;
